import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Box,
  Divider,
  Tooltip,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import BusinessIcon from '@material-ui/icons/Business';
import GroupIcon from '@material-ui/icons/Group';
import GroupWorkIcon from '@material-ui/icons/GroupWork';

import { TeamTreeNode } from './TeamTreeNode';
import { useTeamTreeFromAPI } from './hooks/useTeamTreeFromAPI';

const useStyles = makeStyles(theme => ({
  tableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
  },
  tableHeader: {
    backgroundColor: theme.palette.action.selected,
  },
  tableHeaderCell: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: 13,
    padding: theme.spacing(2),
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td': {
      borderBottom: 'none',
    },
  },
  tableBodyCell: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  teamRowContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  teamIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: 6,
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  teamNameCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  clickableName: {
    cursor: 'pointer',
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: 14,
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  },
  orgIdBadge: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
  },
  descriptionText: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  errorText: {
    color: theme.palette.error.main,
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  sectionHeader: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: theme.spacing(2),
  },
  sectionContainer: {
    marginBottom: theme.spacing(4),
  },
  managedTreesContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
  },
  divider: {
    margin: theme.spacing(4, 0),
  },
  emptyState: {
    padding: theme.spacing(6, 2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  emptyStateIcon: {
    fontSize: 48,
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(2),
  },
  ouBuBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    padding: '2px 6px',
    borderRadius: 3,
    fontSize: 9,
    fontWeight: 500,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.04)',
    color:
      theme.palette.type === 'dark'
        ? theme.palette.text.secondary
        : 'rgba(0, 0, 0, 0.6)',
    border: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.12)'
    }`,
    '& svg': {
      fontSize: 11,
      opacity: 0.7,
    },
  },
}));

export const UserGroupsTable = () => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();

  const { managedTrees, memberTeams, loading, error } = useTeamTreeFromAPI();

  const handleGroupClick = (groupName: string) => {
    navigate(`/catalog/default/Group/${groupName}`);
  };

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
        <Typography style={{ marginLeft: theme.spacing(2) }}>
          Loading your groups...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body1" className={classes.errorText}>
        Error loading groups: {error.message}
      </Typography>
    );
  }

  const hasManagedTeams = managedTrees.length > 0;
  const hasMemberTeams = memberTeams.length > 0;

  if (!hasManagedTeams && !hasMemberTeams) {
    return (
      <Box className={classes.emptyState}>
        <GroupWorkIcon className={classes.emptyStateIcon} />
        <Typography variant="h6" gutterBottom>
          No Teams Found
        </Typography>
        <Typography variant="body2" color="textSecondary">
          You are not a member of any groups.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Managed Teams Section */}
      {hasManagedTeams && (
        <Box className={classes.sectionContainer}>
          <Typography className={classes.sectionHeader}>
            Managed Teams
          </Typography>
          <Box className={classes.managedTreesContainer}>
            {managedTrees.map(tree => (
              <TeamTreeNode
                key={tree.team_id}
                node={tree}
                level={0}
                onTeamClick={handleGroupClick}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Divider between sections */}
      {hasManagedTeams && hasMemberTeams && (
        <Divider className={classes.divider} />
      )}

      {/* Member Teams Section (Flat Table) */}
      {hasMemberTeams && (
        <Box className={classes.sectionContainer}>
          <Typography className={classes.sectionHeader}>
            Other Teams (Member)
          </Typography>
          <Box className={classes.tableContainer}>
            <Table aria-label="member teams table">
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell className={classes.tableHeaderCell}>
                    Team
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {memberTeams.map(group => (
                  <TableRow key={group.name} className={classes.tableRow}>
                    <TableCell className={classes.tableBodyCell}>
                      <Box className={classes.teamRowContent}>
                        <Box className={classes.teamIcon}>
                          <GroupIcon style={{ fontSize: 20 }} />
                        </Box>
                        <Box className={classes.teamNameCell}>
                          <Typography
                            className={classes.clickableName}
                            onClick={() => handleGroupClick(group.name)}
                          >
                            {group.title}
                          </Typography>
                          <Box
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: theme.spacing(1),
                            }}
                          >
                            <Typography className={classes.orgIdBadge}>
                              {group.name.toUpperCase()}
                            </Typography>
                            {(group.ou || group.bu) && (
                              <Box
                                style={{
                                  display: 'flex',
                                  gap: 4,
                                  marginLeft: 4,
                                }}
                              >
                                {group.ou && (
                                  <Tooltip
                                    title="Organizational Unit"
                                    arrow
                                    placement="top"
                                  >
                                    <Box className={classes.ouBuBadge}>
                                      <AccountTreeIcon />
                                      <span>{group.ou}</span>
                                    </Box>
                                  </Tooltip>
                                )}
                                {group.bu && (
                                  <Tooltip
                                    title="Business Unit"
                                    arrow
                                    placement="top"
                                  >
                                    <Box className={classes.ouBuBadge}>
                                      <BusinessIcon />
                                      <span>{group.bu}</span>
                                    </Box>
                                  </Tooltip>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UserGroupsTable;
